import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Facebook,
  Sync,
  Assessment,
  Security,
  Album,
  Timeline,
  Refresh,
  ExpandMore,
  Save,
  Schedule,
  CheckCircle,
  Error,
  Warning
} from '@material-ui/icons';
import Appbar from '../../components/Appbar';
import Footer from '../../components/Footer';
import api from '../../service/api';
import { useHistory } from 'react-router-dom';
import { ArrowBack } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    width: "100vw",
    flexDirection: "column",
    display: "flex",
  },
  content: {
    flex: 1,
    justifyContent: "center",
    display: "flex",
    paddingBottom: "50px",
    paddingTop: theme.spacing(3),
  },
  limit: {
    width: "100%",
    maxWidth: "1376px",
    flexDirection: "column",
    paddingLeft: "15px",
    paddingRight: "15px",
    alignItems: "center",
    display: "flex",
  },
  pageTitle: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  backIcon: {
    marginRight: theme.spacing(1),
  },
  facebookIcon: {
    color: '#1877F2',
    fontSize: '2rem',
  },
  sectionCard: {
    marginBottom: theme.spacing(3),
    width: '100%',
    maxWidth: '800px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  loadingButton: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  infoGrid: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  accordionContent: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
  },
}));

export default function IntegracaoFacebook() {
  const classes = useStyles();
  const history = useHistory();
  
  // Estados principais
  const [loading, setLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', message: '' });
  
  // Estados específicos por funcionalidade
  const [token, setToken] = useState('');
  const [tokenStatus, setTokenStatus] = useState(null);
  const [facebookStatus, setFacebookStatus] = useState(null);
  const [windowsStatus, setWindowsStatus] = useState(null);
  const [specificAlbumId, setSpecificAlbumId] = useState('');

  // Utilitários
  const setLoadingState = (key, value) => {
    setLoading(prev => ({ ...prev, [key]: value }));
  };

  const showSnackbar = (message, severity = 'info') => {
    // Garantir que message seja sempre string
    let displayMessage;
    if (typeof message === 'object' && message !== null) {
      displayMessage = message.message || message.statusCode || 'Erro desconhecido';
    } else {
      displayMessage = message || 'Erro desconhecido';
    }
    setSnackbar({ open: true, message: displayMessage, severity });
  };

  const showConfirmDialog = (title, message, action) => {
    setConfirmDialog({ open: true, title, message, action });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // API Calls

  // 1. POST /api/facebook/token
  const saveToken = async () => {
    if (!token.trim()) {
      showSnackbar('Por favor, insira um token válido', 'warning');
      return;
    }

    setLoadingState('saveToken', true);
    try {
      const payload = { accessToken: token };
      
      const response = await api.post('/facebook/token', payload);
      showSnackbar(response.data.message || 'Token salvo com sucesso!', 'success');
      setToken('');
      validateToken();
      fetchFacebookStatus();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao salvar token';
      showSnackbar(message, 'error');
    } finally {
      setLoadingState('saveToken', false);
    }
  };

  // 2. GET /api/facebook/validate-token
  const validateToken = useCallback(async () => {
    setLoadingState('validateToken', true);
    try {
      const response = await api.get('/facebook/validate-token');
      console.log('Validate Token Response:', response.data);
      setTokenStatus(response.data.data);
      showSnackbar(response.data.message, response.data.valid ? 'success' : 'warning');
    } catch (error) {
      console.log('Validate Token Error:', error);
      let message;
      if (error.response?.status === 401) {
        message = 'Token de autenticação inválido';
      } else if (error.response?.status === 404) {
        message = 'Endpoint de validação não encontrado';
      } else {
        message = error.response?.data?.message || error.message || 'Erro ao validar token';
      }
      showSnackbar(message, 'warning');
      setTokenStatus({ valid: false, message: 'Token não validado' });
    } finally {
      setLoadingState('validateToken', false);
    }
  }, []);

  // 3. GET /api/facebook/status
  const fetchFacebookStatus = useCallback(async (skipMessage = false) => {
    setLoadingState('facebookStatus', true);
    try {
      const response = await api.get('/facebook/status');
      console.log('Facebook Status Response:', response.data);
      setFacebookStatus(response.data.data);
    } catch (error) {
      console.log('Facebook Status Error:', error);
      let message;
      if (error.response?.status === 401) {
        message = 'Token de autenticação inválido ou expirado';
      } else if (error.response?.status === 404) {
        message = 'Endpoint não encontrado. Verifique se a API está atualizada.';
      } else {
        message = error.response?.data?.message || error.message || 'Erro ao buscar status';
      }
      if (!skipMessage) {
        showSnackbar(message, 'error');
      }
      setFacebookStatus(null);
    } finally {
      setLoadingState('facebookStatus', false);
    }
  }, []);

  // 4. GET /api/facebook/sync/windows-status
  const fetchWindowsStatus = useCallback(async () => {
    setLoadingState('windowsStatus', true);
    try {
      const response = await api.get('/facebook/sync/windows-status');
      setWindowsStatus(response.data);
    } catch (error) {
      console.log('Windows Status Error:', error);
      let message;
      if (error.response?.status === 404) {
        message = 'Endpoint de status das janelas não encontrado.';
      } else {
        message = error.response?.data?.message || error.message || 'Erro ao buscar status das janelas';
      }
      showSnackbar(message, 'warning');
      setWindowsStatus(null);
    } finally {
      setLoadingState('windowsStatus', false);
    }
  }, []);

  // 5. GET /api/facebook/sync/config
  const fetchSyncConfig = useCallback(async (skipMessage = false) => {
    setLoadingState('syncConfig', true);
    try {
      const response = await api.get('/facebook/sync/config');
      // Config fetched but not stored since UI is disabled
      console.log('Sync config fetched:', response.data);
    } catch (error) {
      console.log('Sync Config Error:', error);
      let message;
      if (error.response?.status === 404) {
        message = 'Endpoint de configuração não encontrado.';
      } else {
        message = error.response?.data?.message || error.message || 'Erro ao buscar configuração';
      }
      if (!skipMessage) {
        showSnackbar(message, 'warning');
      }
    } finally {
      setLoadingState('syncConfig', false);
    }
  }, []);



  // 8. POST /api/facebook/sync/albums
  const syncAllAlbums = async () => {
    setLoadingState('syncAllAlbums', true);
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const response = await api.post('/facebook/sync/albums');
      showSnackbar(
        `${response.data.data.message} - Sincronizados: ${response.data.data.synced}, Erros: ${response.data.data.errors}`,
        'success'
      );
      fetchFacebookStatus();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro na sincronização de álbuns';
      showSnackbar(message, 'error');
    } finally {
      setLoadingState('syncAllAlbums', false);
    }
  };

  // 9. POST /api/facebook/sync/album/:fbId
  const syncSpecificAlbum = async () => {
    if (!specificAlbumId.trim()) {
      showSnackbar('Por favor, insira um ID de álbum válido', 'warning');
      return;
    }

    setLoadingState('syncSpecificAlbum', true);
    try {
      const response = await api.post(`/facebook/sync/album/${specificAlbumId}`);
      showSnackbar(response.data.message || 'Álbum sincronizado com sucesso!', 'success');
      setSpecificAlbumId('');
      fetchFacebookStatus();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro na sincronização do álbum';
      showSnackbar(message, 'error');
    } finally {
      setLoadingState('syncSpecificAlbum', false);
    }
  };

  // 10. POST /api/facebook/sync/timeline
  const syncTimeline = async () => {
    setLoadingState('syncTimeline', true);
    setConfirmDialog({ ...confirmDialog, open: false });
    try {
      const response = await api.post('/facebook/sync/timeline');
      showSnackbar(
        `${response.data.data.message} - Sincronizados: ${response.data.data.synced}, Erros: ${response.data.data.errors}`,
        'success'
      );
      fetchFacebookStatus();
      fetchWindowsStatus();
    } catch (error) {
      const message = error.response?.data?.message || 'Erro na sincronização da timeline';
      showSnackbar(message, 'error');
    } finally {
      setLoadingState('syncTimeline', false);
    }
  };

  // Effects
  useEffect(() => {
    let isMounted = true;
    
    const initializeData = async () => {
      if (isMounted) {
        // Apenas buscar dados essenciais no início, sem mostrar erros
        await fetchFacebookStatus(true);
        await fetchSyncConfig(true);
      }
    };

    initializeData();

    // Cleanup para evitar memory leaks
    return () => {
      isMounted = false;
    };
  }, [fetchFacebookStatus, fetchSyncConfig]);

  return (
    <div className={classes.root}>
      <Appbar />
      <div className={classes.content}>
        <div className={classes.limit}>
          <Box className={classes.pageTitle}>
            <IconButton aria-label="voltar" onClick={() => history.push('/configuracoes')} className={classes.backIcon}>
              <ArrowBack />
            </IconButton>
            <Facebook className={classes.facebookIcon} />
            <Typography variant="h4">Integração Facebook</Typography>
          </Box>

          {/* Seção 1: Gerenciamento de Token */}
          <Card className={classes.sectionCard}>
            <CardContent>
              <Box className={classes.cardHeader}>
                <Security color="primary" />
                <Typography variant="h6">Gerenciamento de Token</Typography>
              </Box>

              <TextField
                fullWidth
                label="Token de Acesso do Facebook"
                placeholder="Cole aqui o token de acesso..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                variant="outlined"
                multiline
                minRows={3}
                margin="normal"
              />

              <Box className={classes.buttonGroup} mt={2}>
                <div className={classes.loadingButton}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveToken}
                    disabled={loading.saveToken || !token.trim()}
                    startIcon={<Save />}
                  >
                    Salvar Token
                  </Button>
                  {loading.saveToken && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>

                <div className={classes.loadingButton}>
                  <Button
                    variant="outlined"
                    onClick={validateToken}
                    disabled={loading.validateToken}
                    startIcon={<CheckCircle />}
                  >
                    Validar Token
                  </Button>
                  {loading.validateToken && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>
              </Box>

              {tokenStatus && (
                <Box mt={2}>
                  <Chip
                    className={classes.statusChip}
                    icon={tokenStatus.valid ? <CheckCircle /> : <Error />}
                    label={tokenStatus.message}
                    color={tokenStatus.valid ? 'primary' : 'secondary'}
                    variant={tokenStatus.valid ? 'default' : 'outlined'}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Seção 2: Status e Monitoramento */}
          <Card className={classes.sectionCard}>
            <CardContent>
              <Box className={classes.cardHeader}>
                <Assessment color="primary" />
                <Typography variant="h6">Status e Monitoramento</Typography>
              </Box>

              <Box className={classes.buttonGroup}>
                <div className={classes.loadingButton}>
                  <Button
                    variant="outlined"
                    onClick={fetchFacebookStatus}
                    disabled={loading.facebookStatus}
                    startIcon={<Refresh />}
                  >
                    Status Geral
                  </Button>
                  {loading.facebookStatus && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>

                <div className={classes.loadingButton}>
                  <Button
                    variant="outlined"
                    onClick={fetchWindowsStatus}
                    disabled={loading.windowsStatus}
                    startIcon={<Schedule />}
                  >
                    Status Janelas de Sync
                  </Button>
                  {loading.windowsStatus && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>
              </Box>

              {/* Status Geral */}
              {facebookStatus && (
                <Accordion style={{ marginTop: 16 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">Status Geral do Facebook</Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionContent}>
                    <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        className={classes.statusChip}
                        label={facebookStatus.status}
                        color={facebookStatus.tokenValid ? 'primary' : 'secondary'}
                        icon={facebookStatus.tokenValid ? <CheckCircle /> : <Warning />}
                      />
                      {facebookStatus.tokenInfo && (
                        <>
                          <Chip
                            className={classes.statusChip}
                            label={`Token: ${facebookStatus.tokenInfo.isValid ? 'Válido' : 'Inválido'}`}
                            color={facebookStatus.tokenInfo.isValid ? 'primary' : 'secondary'}
                            size="small"
                          />
                          <Chip
                            className={classes.statusChip}
                            label={`Expira: ${formatDate(facebookStatus.tokenInfo.dataAccessExpiresAt)}`}
                            color="default"
                            size="small"
                          />
                        </>
                      )}
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Usuário:</Typography>
                        <Typography variant="body2">
                          {facebookStatus.facebookUser?.name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Email:</Typography>
                        <Typography variant="body2">
                          {facebookStatus.facebookUser?.email || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Álbuns:</Typography>
                        <Typography variant="body2">
                          {facebookStatus.database?.albumsCount || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2">Fotos:</Typography>
                        <Typography variant="body2">
                          {facebookStatus.database?.photosCount || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Última Sincronização:</Typography>
                        <Typography variant="body2">
                          {formatDate(facebookStatus.database?.lastSync)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Status das Janelas */}
              {windowsStatus && (
                <Accordion style={{ marginTop: 8 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1">Status das Janelas de Sincronização</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Janela</TableCell>
                            <TableCell>Última Execução</TableCell>
                            <TableCell>Duração</TableCell>
                            <TableCell>Sincronizados</TableCell>
                            <TableCell>Erros</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {windowsStatus.windows && Object.entries(windowsStatus.windows).map(([type, data]) => (
                            <TableRow key={type}>
                              <TableCell>{type}</TableCell>
                              <TableCell>{formatDate(data.lastRunAt)}</TableCell>
                              <TableCell>{formatDuration(data.lastDurationSeconds)}</TableCell>
                              <TableCell>{data.synced}</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={data.errors} 
                                  color={data.errors > 0 ? 'secondary' : 'primary'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </CardContent>
          </Card>

          {/* Seção 3: Configurações de Sincronização */}
          {/*
          <Card className={classes.sectionCard}>
            <CardContent>
              <Box className={classes.cardHeader}>
                <Settings color="primary" />
                <Typography variant="h6">Configurações de Sincronização</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Janela de Dias (1-60)"
                    type="number"
                    value={syncConfig.dailyWindowDays}
                    onChange={(e) => setSyncConfig({ ...syncConfig, dailyWindowDays: parseInt(e.target.value) })}
                    variant="outlined"
                    inputProps={{ min: 1, max: 60 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Horário Diário (HH:mm)"
                    type="time"
                    value={syncConfig.dailyTime}
                    onChange={(e) => setSyncConfig({ ...syncConfig, dailyTime: e.target.value })}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <Box className={classes.buttonGroup} mt={2}>
                <div className={classes.loadingButton}>
                  <Button
                    variant="outlined"
                    onClick={fetchSyncConfig}
                    disabled={loading.syncConfig}
                    startIcon={<Refresh />}
                  >
                    Carregar Configuração
                  </Button>
                  {loading.syncConfig && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>

                <div className={classes.loadingButton}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={saveSyncConfig}
                    disabled={loading.saveSyncConfig}
                    startIcon={<Save />}
                  >
                    Salvar Configuração
                  </Button>
                  {loading.saveSyncConfig && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </div>
              </Box>
            </CardContent>
          </Card>
          */} 
          {/* Seção 4: Sincronização */}
          <Card className={classes.sectionCard}>
            <CardContent>
              <Box className={classes.cardHeader}>
                <Sync color="primary" />
                <Typography variant="h6">Operações de Sincronização</Typography>
              </Box>

              {/* Preview de Álbuns */}
              {/*
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Visibility />
                    <Typography variant="subtitle1">Preview de Álbuns</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionContent}>
                  <div className={classes.loadingButton}>
                    <Button
                      variant="outlined"
                      onClick={fetchAlbumsPreview}
                      disabled={loading.albumsPreview}
                      startIcon={<Album />}
                    >
                      Carregar Preview
                    </Button>
                    {loading.albumsPreview && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </div>

                  {albumPreview.length > 0 && (
                    <TableContainer className={classes.tableContainer}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>Fotos</TableCell>
                            <TableCell>Criado</TableCell>
                            <TableCell>Atualizado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {albumPreview.map((album) => (
                            <TableRow key={album.id}>
                              <TableCell>{album.id}</TableCell>
                              <TableCell>{album.name}</TableCell>
                              <TableCell>{album.count}</TableCell>
                              <TableCell>{formatDate(album.created_time)}</TableCell>
                              <TableCell>{formatDate(album.updated_time)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
              */}
              {/* Sincronização de Álbuns */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Album />
                    <Typography variant="subtitle1">Sincronizar Álbuns</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionContent}>
                  <Box className={classes.formRow}>
                    <TextField
                      label="ID do Álbum (específico)"
                      value={specificAlbumId}
                      onChange={(e) => setSpecificAlbumId(e.target.value)}
                      variant="outlined"
                      placeholder="123456789012345"
                      style={{ flexGrow: 1 }}
                    />
                    <div className={classes.loadingButton}>
                      <Button
                        variant="outlined"
                        onClick={syncSpecificAlbum}
                        disabled={loading.syncSpecificAlbum || !specificAlbumId.trim()}
                        startIcon={<Sync />}
                      >
                        Sincronizar
                      </Button>
                      {loading.syncSpecificAlbum && (
                        <CircularProgress size={24} className={classes.buttonProgress} />
                      )}
                    </div>
                  </Box>

                  <div className={classes.loadingButton}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => showConfirmDialog(
                        'Sincronizar Todos os Álbuns',
                        'Tem certeza que deseja sincronizar todos os álbuns? Esta operação pode demorar alguns minutos.',
                        syncAllAlbums
                      )}
                      disabled={loading.syncAllAlbums}
                      startIcon={<Album />}
                    >
                      Sincronizar Todos os Álbuns
                    </Button>
                    {loading.syncAllAlbums && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* Sincronização de Timeline */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Timeline />
                    <Typography variant="subtitle1">Sincronizar Timeline</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionContent}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Força a sincronização da timeline dos últimos 60 dias.
                  </Typography>
                  
                  <div className={classes.loadingButton}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => showConfirmDialog(
                        'Forçar Sincronização da Timeline',
                        'Tem certeza que deseja forçar a sincronização da timeline? Esta operação sincronizará os últimos 60 dias e pode demorar alguns minutos.',
                        syncTimeline
                      )}
                      disabled={loading.syncTimeline}
                      startIcon={<Timeline />}
                    >
                      Forçar Sincronização Timeline
                    </Button>
                    {loading.syncTimeline && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />

      {/* Dialog de Confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancelar
          </Button>
          <Button 
            onClick={confirmDialog.action} 
            color="primary" 
            variant="contained"
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
}
